import Grid from "@mui/material/Grid";
import {Box, Typography} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import AddEditBranchDialog from './components/AddEditBranchDialog';
import DeleteConfirmDialog from '../../components/Dialogs/DeleteConfirmDialog';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SoftButton from "components/SoftButton";
import SoftInput from "../../components/SoftInput";
import {addBranch, deleteBranch, getAllBranches, updateBranch} from "../../api/branch/branchApiClient";
import BranchCard from "./components/BranchCard";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoGenerator from "../../utils/logoGenerator";
import {useRayzayUIController} from "../../context";
import {getCompanyById} from "../../api/company/companyApiClient";
import ViewCompanyDialog from "../company/components/ViewCompanyDialog";
import {handleApiError} from "../../utils/notificationUtils";

function Overview() {
    const [controller] = useRayzayUIController();
    const {loggedInUserDetail} = controller;
    const [branches, setBranches] = useState([]);
    const [filteredBranches, setFilteredBranches] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [companyToView, setCompanyToView] = useState(null);
    const [viewCompanyDialogOpen, setViewCompanyDialogOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isMountedRef = useRef(true);

    // Check if we're on a child route (branch details)
    const isOnChildRoute = location.pathname.includes('/branches/') && location.pathname !== '/branches';

    const loadBranches = useCallback(async () => {
        try {
            const response = await getAllBranches();
            if (isMountedRef.current) {
                // Sort branches by ID to ensure consistent ordering regardless of backend response order
                const sortedBranches = response.data.sort((a, b) => a.branchId - b.branchId);
                setBranches(sortedBranches);
                setFilteredBranches(sortedBranches);
            }
        } catch (error) {
            console.error('Error loading branches:', error);
        }
    }, []);

    useEffect(() => {
        loadBranches().then();
    }, [loadBranches]);

    // Re-filter whenever the source list or the query changes. Match
    // name, location, description, address, or contact details.
    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) {
            setFilteredBranches(branches);
            return;
        }
        setFilteredBranches(
            branches.filter((b) => {
                const haystack = [
                    b.name,
                    b.companyName,
                    b.location,
                    b.description,
                    b.address,
                    b.phoneNumber,
                    b.email,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(q);
            })
        );
    }, [branches, searchQuery]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);


    const handleAddBranch = () => {
        setSelectedBranch(null);
        setEditMode(false);
        setAddEditDialogOpen(true);
    };

    const handleEditBranch = (branch) => {
        setSelectedBranch(branch);
        setEditMode(true);
        setAddEditDialogOpen(true);
    };

    const handleDeleteBranch = (branch) => {
        setSelectedBranch(branch);
        setDeleteDialogOpen(true);
    };

    const handleCompanyNameClick = async (branch) => {
        try {
            // Check if company object exists, otherwise fetch using companyId
            if (branch.company) {
                setCompanyToView(branch.company);
                setViewCompanyDialogOpen(true);
            } else if (branch.companyId) {
                const response = await getCompanyById(branch.companyId);
                if (response && response.data) {
                    setCompanyToView(response.data);
                    setViewCompanyDialogOpen(true);
                } else {
                    console.warn("Company data not found for ID:", branch.companyId);
                    handleApiError(new Error("Company details not found."), "Failed to load company details.");
                }
            } else {
                console.warn("No company or companyId found for branch:", branch.branchId);
                handleApiError(new Error("Company details not available."), "Company details not available.");
            }
        } catch (error) {
            console.error('Error loading company details:', error);
            handleApiError(error, 'Failed to load company details. Please try again.');
        }
    };

    const handleOnSubmitBranch = async (formData) => {
        try {
            if (editMode) {
                await updateBranch(selectedBranch.branchId, formData);
            } else {
                await addBranch(formData);
            }
            await loadBranches();
            if (isMountedRef.current) {
                setAddEditDialogOpen(false); // Only close dialog on success
            }
        } catch (error) {
            console.error('Error saving branch:', error);
            // Re-throw the error so the dialog can handle it and display the proper error message
            // The AddEditBranchDialog component will catch this and display it via handleApiError
            throw error;
        }
    };

    function onDeleteBranch() {
        deleteBranch(selectedBranch.branchId).then(() => {
            loadBranches().then();
            if (isMountedRef.current) {
                setDeleteDialogOpen(false);
                setSelectedBranch(null);
            }
        }).catch(
            error => {
                console.error('Error deleting branch:', error);
            }
        );
    }
    return (
        <DashboardLayout>
            <SoftBox mb={3}>
                    <SoftBox pt={2} px={2}>
                        <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                            <SoftBox display="flex" alignItems="center" gap={2} flexShrink={0}>
                                {isOnChildRoute && (
                                    <IconButton
                                        onClick={() => navigate('/branches')}
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            }
                                        }}
                                    >
                                        <ArrowBackIcon/>
                                    </IconButton>
                                )}
                                <SoftTypography variant="h6" fontWeight="medium">
                                    {isOnChildRoute ? 'Branch Details' : 'Branches'}
                                </SoftTypography>
                            </SoftBox>
                            {!isOnChildRoute && (
                                <SoftBox flexGrow={1} maxWidth={560}>
                                    <SoftInput
                                        placeholder="Type here to filter by name, company, location, address or contact"
                                        icon={{component: "search", direction: "left"}}
                                        value={searchQuery}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                    />
                                </SoftBox>
                            )}
                            {/* Hide "Add Branch" button for BRANCH_ADMIN users - they don't have permission to create branches */}
                            {!isOnChildRoute && loggedInUserDetail?.userRole !== "BRANCH_ADMIN" && (
                                <SoftButton color="primary" onClick={handleAddBranch} sx={{flexShrink: 0}}>
                                    Add Branch
                                </SoftButton>
                            )}
                        </SoftBox>
                    </SoftBox>
                    <SoftBox p={2}>
                        {isOnChildRoute ? (
                            <Outlet/>
                        ) : filteredBranches.length === 0 ? (
                            <SoftBox
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                minHeight="200px"
                                p={3}
                                sx={{
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        backgroundColor: 'grey.100',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '24px',
                                            color: 'grey.400',
                                        }}
                                    >
                                        📊
                                    </Typography>
                                </Box>
                                <SoftTypography
                                    variant="h6"
                                    color="text"
                                    textAlign="center"
                                    fontWeight="medium"
                                    mb={1}
                                >
                                    No branches found
                                </SoftTypography>
                                <SoftTypography
                                    variant="body2"
                                    color="text"
                                    opacity={0.6}
                                    textAlign="center"
                                >
                                    Try adjusting your filters or add some data to get started.
                                </SoftTypography>
                            </SoftBox>
                        ) : (
                            <Grid container spacing={3}>
                                {filteredBranches.map((branch) => {
                                    try {
                                        console.log('Processing branch:', branch?.name, 'Location:', branch?.location, 'ID:', branch?.branchId);
                                        const locationToUse = branch?.location || branch?.name;
                                        console.log('Using location for logo:', locationToUse);
                                        const branchLogo = LogoGenerator.generateLocationLogo(locationToUse);
                                        console.log('Generated logo URL length:', branchLogo?.length);
                                        return (
                                            <Grid item xs={12} md={6} xl={3} key={`branch-${branch.branchId}`}>
                                                <BranchCard
                                                    image={branchLogo}
                                                    branch={branch}
                                                    onCompanyClick={handleCompanyNameClick}
                                                    action={{
                                                        type: "internal",
                                                        route: `/branches/${branch.branchId}`,
                                                        color: "info",
                                                        label: "view branch",
                                                    }}
                                                    actions={[
                                                        <IconButton key="edit" onClick={e => {
                                                            e.preventDefault();
                                                            handleEditBranch(branch);
                                                        }} size="small" color="info"><EditIcon/></IconButton>,
                                                        // Hide delete button for BRANCH_ADMIN users - they don't have permission to delete branches
                                                        loggedInUserDetail?.userRole !== "BRANCH_ADMIN" && (
                                                            <IconButton key="delete" onClick={e => {
                                                                e.preventDefault();
                                                                handleDeleteBranch(branch);
                                                            }} size="small" color="error"><DeleteIcon/></IconButton>
                                                        )
                                                    ].filter(Boolean)}
                                                />
                                            </Grid>
                                        );
                                    } catch (error) {
                                        console.error('Error rendering branch card:', error, branch);
                                        return null;
                                    }
                                })}
                            </Grid>
                        )}
                    </SoftBox>
            </SoftBox>
            <AddEditBranchDialog
                open={addEditDialogOpen}
                onClose={() => setAddEditDialogOpen(false)}
                branch={selectedBranch}
                isEdit={editMode}
                onSubmit={handleOnSubmitBranch}/>
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onDelete={onDeleteBranch}
                warningMessage={`Are you sure you want to delete branch '${selectedBranch?.name}'? This action cannot be undone.`}
            />
            <ViewCompanyDialog
                open={viewCompanyDialogOpen}
                onClose={() => {
                    setViewCompanyDialogOpen(false);
                    setCompanyToView(null);
                }}
                company={companyToView}
            />
        </DashboardLayout>
    );
}

export default Overview;
